import { useLayoutEffect, useRef, useState } from "react";
import type { StickyCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import {
  STICKY_FONT_DEFAULT_PX,
  STICKY_SIGNATURE_RESERVE_PX,
  measureStickyFitFontSize,
} from "../../../lib/sticky-font-fit";

interface Props {
  readonly object: StickyCanvasObject;
}

// 付箋オブジェクト。
// - 配置位置に貼り付き、いつでも誰でも文字編集可能
// - サーバへは object.text.update op をキー入力後 50ms debounce で送信する
// - 単一クリックで選択、ダブルクリックで編集モードへ
export function StickyObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
    disabled: object.locked,
  });

  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: editor.isEditing || activeTool === "draw-pen",
  });

  // 付箋本文の文字サイズを、付箋サイズへちょうど収まるよう自動調整する（余白を最小化する）。
  // - 編集を終了したタイミング（isEditing が false へ遷移）に再計算するのが主目的。
  // - リサイズ確定や他参加者の本文更新で w / h / text が変わったときにも追従する。
  // 編集中（isEditing=true）は再計算せず直前のサイズを保持し、入力中にサイズが暴れないようにする。
  const [fittedFontSize, setFittedFontSize] = useState<number>(STICKY_FONT_DEFAULT_PX);
  useLayoutEffect(() => {
    if (editor.isEditing) return;
    const sample = editorRef.current;
    if (!sample) return;
    const next = measureStickyFitFontSize({
      text: object.text,
      boxWidth: object.w,
      boxHeight: object.h,
      hasSignature: Boolean(object.updatedByName),
      sample,
    });
    setFittedFontSize(next);
  }, [editor.isEditing, object.text, object.w, object.h, object.updatedByName]);

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="sticky"
      onDoubleClick={editor.enterEditMode}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: object.color ?? "#fff5b3",
        boxShadow: isSelected
          ? "0 0 0 2px var(--color-accent), 0 2px 6px rgba(0,0,0,0.15)"
          : "0 2px 6px rgba(0,0,0,0.15)",
        padding: "12px",
        // 文字サイズは付箋サイズと本文から自動算出した値を用いる（編集終了時などに再計算）。
        fontSize: `${fittedFontSize}px`,
        whiteSpace: "pre-wrap",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        cursor: editor.isEditing ? "text" : "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: editor.isEditing ? "text" : "none",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: "28px",
          height: "28px",
          background: "linear-gradient(135deg, rgba(255,255,255,0.6) 0 50%, rgba(0,0,0,0.12) 51% 100%)",
          clipPath: "polygon(100% 0, 100% 100%, 0 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        ref={editorRef}
        data-testid={`canvas-object-text-${object.id}`}
        style={{
          width: "100%",
          height: "100%",
          outline: "none",
          cursor: "inherit",
          // 文字寄せ（左/中央/右）。未指定は左寄せ。編集中・確定後の両方に適用する。
          textAlign: object.textAlign ?? "left",
          // 長い英単語などもはみ出さずに折り返す（自動調整の高さ計測と表示の折り返しを一致させる）。
          overflowWrap: "anywhere",
          // 最終更新者名と付箋本文がぶつからないよう下側にパディングを取る。
          // 自動調整時の高さ計算（STICKY_SIGNATURE_RESERVE_PX）と同じ値を使い、本文が署名へ干渉しないようにする。
          paddingBottom: object.updatedByName ? `${STICKY_SIGNATURE_RESERVE_PX}px` : undefined,
          boxSizing: "border-box",
        }}
        {...editor.editorProps}
      >
        {editor.isEditing ? null : object.text}
      </div>
      {object.updatedByName ? (
        // 左下に薄く小さく署名表示。通常テキストの「小」サイズより小さい固定 11px で、
        // めくれ三角の左側に収まるよう右側に十分マージンを取る。
        <div
          aria-label={`最終更新者: ${object.updatedByName}`}
          style={{
            position: "absolute",
            left: "8px",
            bottom: "4px",
            // めくれ三角（右下 28×28）と被らないよう、署名側にも右マージンを設ける。
            right: "32px",
            fontSize: "11px",
            color: "rgba(0,0,0,0.45)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {object.updatedByName}
        </div>
      ) : null}
    </div>
  );
}
